import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

interface CoordinateFinderProps {
  imageSrc: string;
  onCoordinatesFound: (coordinates: Array<{id: string, x: number, y: number, width: number, height: number, label: string}>) => void;
}

const CoordinateFinder: React.FC<CoordinateFinderProps> = ({ imageSrc, onCoordinatesFound }) => {
  const [showGrid, setShowGrid] = useState(true);
  const [showCoordinates, setShowCoordinates] = useState(true);
  const [hotspots, setHotspots] = useState<Array<{id: string, x: number, y: number, width: number, height: number, label: string}>>([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentHotspot, setCurrentHotspot] = useState<{x: number, y: number, width: number, height: number} | null>(null);
  const [hotspotLabel, setHotspotLabel] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!imageRef.current) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setCurrentHotspot({ x, y, width: 0, height: 0 });
    setIsDrawing(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDrawing || !currentHotspot || !imageRef.current) return;
    
    const rect = imageRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setCurrentHotspot(prev => prev ? {
      ...prev,
      width: Math.abs(x - prev.x),
      height: Math.abs(y - prev.y)
    } : null);
  };

  const handleMouseUp = () => {
    if (currentHotspot && hotspotLabel.trim()) {
      const newHotspot = {
        id: `hotspot-${Date.now()}`,
        ...currentHotspot,
        label: hotspotLabel
      };
      setHotspots(prev => [...prev, newHotspot]);
      setHotspotLabel('');
    }
    setIsDrawing(false);
    setCurrentHotspot(null);
  };

  const removeHotspot = (id: string) => {
    setHotspots(prev => prev.filter(h => h.id !== id));
  };

  const exportCoordinates = () => {
    onCoordinatesFound(hotspots);
  };

  return (
    <div className="max-w-4xl mx-auto p-4">
      <div className="mb-6 bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-4">🎯 Interactive Coordinate Finder</h2>
        <p className="text-gray-600 mb-4">
          Click and drag on the image to create hotspots. The coordinates will be calculated automatically!
        </p>
        
        <div className="flex gap-4 mb-4">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => setShowGrid(e.target.checked)}
              className="rounded"
            />
            Show Grid
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={showCoordinates}
              onChange={(e) => setShowCoordinates(e.target.checked)}
              className="rounded"
            />
            Show Coordinates
          </label>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Enter hotspot label (e.g., 'Swimming Pool')"
            value={hotspotLabel}
            onChange={(e) => setHotspotLabel(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg"
          />
        </div>
      </div>

      <div 
        ref={containerRef}
        className="relative bg-gray-100 rounded-lg overflow-hidden"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <img
          ref={imageRef}
          src={imageSrc}
          alt="House for coordinate finding"
          className="w-full h-auto"
          draggable={false}
        />

        {/* Grid overlay */}
        {showGrid && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 10 }, (_, i) => (
              <React.Fragment key={i}>
                <div 
                  className="absolute bg-red-500/20"
                  style={{
                    left: `${i * 10}%`,
                    top: 0,
                    width: '1px',
                    height: '100%'
                  }}
                />
                <div 
                  className="absolute bg-red-500/20"
                  style={{
                    left: 0,
                    top: `${i * 10}%`,
                    width: '100%',
                    height: '1px'
                  }}
                />
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Coordinate labels */}
        {showCoordinates && (
          <div className="absolute inset-0 pointer-events-none">
            {Array.from({ length: 11 }, (_, i) => (
              <React.Fragment key={i}>
                <div 
                  className="absolute text-xs bg-white/80 px-1 rounded"
                  style={{ left: `${i * 10}%`, top: 0 }}
                >
                  {i * 10}%
                </div>
                <div 
                  className="absolute text-xs bg-white/80 px-1 rounded"
                  style={{ left: 0, top: `${i * 10}%` }}
                >
                  {i * 10}%
                </div>
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Current drawing hotspot */}
        {currentHotspot && (
          <div
            className="absolute border-2 border-blue-500 bg-blue-500/20"
            style={{
              left: `${Math.min(currentHotspot.x, currentHotspot.x + currentHotspot.width)}%`,
              top: `${Math.min(currentHotspot.y, currentHotspot.y + currentHotspot.height)}%`,
              width: `${currentHotspot.width}%`,
              height: `${currentHotspot.height}%`,
            }}
          />
        )}

        {/* Existing hotspots */}
        {hotspots.map((hotspot) => (
          <motion.div
            key={hotspot.id}
            className="absolute border-2 border-green-500 bg-green-500/20 hover:bg-green-500/40 cursor-pointer group"
            style={{
              left: `${hotspot.x}%`,
              top: `${hotspot.y}%`,
              width: `${hotspot.width}%`,
              height: `${hotspot.height}%`,
            }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileHover={{ scale: 1.05 }}
          >
            <div className="absolute -top-6 left-0 bg-green-500 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity">
              {hotspot.label}
            </div>
            <button
              onClick={() => removeHotspot(hotspot.id)}
              className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs hover:bg-red-600"
            >
              ×
            </button>
          </motion.div>
        ))}
      </div>

      {/* Results */}
      {hotspots.length > 0 && (
        <div className="mt-6 bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-bold mb-4">📍 Found Coordinates</h3>
          <div className="space-y-2 mb-4">
            {hotspots.map((hotspot) => (
              <div key={hotspot.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="font-medium">{hotspot.label}</span>
                <span className="text-sm text-gray-600">
                  x: {hotspot.x.toFixed(1)}%, y: {hotspot.y.toFixed(1)}%, 
                  w: {hotspot.width.toFixed(1)}%, h: {hotspot.height.toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
          
          <div className="flex gap-4">
            <button
              onClick={exportCoordinates}
              className="btn-primary"
            >
              Export Coordinates
            </button>
            <button
              onClick={() => setHotspots([])}
              className="btn-secondary"
            >
              Clear All
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CoordinateFinder;
